import type { AnyPresentationOption, OptionsDocument, QuoteDocument } from 'paintscout'
import { getFeature } from '../../../options/get-features'
import { getDefaultPresentation } from '../../../options/presentation/get-default-presentation'
import { isPresentationNext } from '../is-presentation-next'

// A presentation with no pages crashes the renderer (it indexes pages[pageIndex].sections),
// so any candidate that lacks pages must fall through to a default presentation.
function hasPages(presentation?: AnyPresentationOption): boolean {
  return Array.isArray(presentation?.pages) && presentation.pages.length > 0
}

// NOTE: this used to append synthetic Sherwin-Williams/Benjamin Moore partner pages to active
// v1 templates (gated on the partnerContent feature). That merge was dead code — the injected
// pages carried no `active`/`advanced.useForNav`, so the v1 renderer's stacked-page filter and
// the nav filter both dropped them — but it leaked visibly through the v2 renderer (no
// page-active filter) whenever a synthetic presentation took the v1 path (e.g. the imbed
// previews). Removed in #21311; partner sections are added explicitly in the editors instead.

export function getQuotePresentation(args: {
  options: OptionsDocument
  quote: QuoteDocument
  inactive?: boolean // return inactive presentations
  isWorkOrder?: boolean
  isProductOrderForm?: boolean
  current?: boolean // return current presentations from options(sb feature)
}): AnyPresentationOption {
  const { quote, options, isWorkOrder, inactive, current } = args
  const presentationsFeature = getFeature({ options, path: 'presentation.enabled' })
  const currentPresentation = options.options?.presentationOptions?.values?.[quote?.presentationTemplate?.key]
  const currentPresentationV2 = options.options?.presentationOptionsV2?.values?.[quote?.presentationTemplate?.key]

  const presentationsOnInvoices = getFeature({ options, path: 'presentation.presentationsOnInvoices' })

  if (isWorkOrder) {
    // down the road - we will probably beef-up the work-order
    // to have some presentation capabilities
    // ie: maps, etc
    return getDefaultPresentation({ options, isWorkOrder })
  } else if (quote?.is_invoice && (!presentationsOnInvoices || !presentationsFeature)) {
    return getDefaultPresentation({ options, invoice: quote.is_invoice })
  } else if (quote?.presentationTemplate && inactive && hasPages(quote.presentationTemplate)) {
    return quote.presentationTemplate
  } else if (current && currentPresentation?.key && hasPages(currentPresentation)) {
    return currentPresentation
  } else if (current && currentPresentationV2?.key && hasPages(currentPresentationV2)) {
    return currentPresentationV2
  } else if (quote?.presentationTemplate?.active) {
    // An active template can still be missing pages (e.g. a presentation stored as a
    // { key, active } reference). Returning it would crash the renderer, so fall back.
    // Log the swap so this degraded state is diagnosable rather than a silent change.
    if (!hasPages(quote.presentationTemplate)) {
      const isV2 = isPresentationNext({ options, presentation: quote.presentationTemplate })
      console.warn(
        `getQuotePresentation: active ${isV2 ? 'v2 ' : ''}presentationTemplate "${quote.presentationTemplate.key}" ` +
          `has no pages; falling back to the default presentation`
      )
      return getDefaultPresentation({ options, invoice: quote?.is_invoice })
    }

    return quote.presentationTemplate
  } else {
    return getDefaultPresentation({ options, invoice: quote?.is_invoice })
  }
}
